/**
 * Hub Routing — Real provider/model/pricing data from Cortex Conductor.
 *
 * Source: cortex/conductor/config.py (production configuration).
 * All prices in $/MTok (million tokens).
 */

export interface ModelInfo {
  id: string;
  displayName: string;
  provider: string;
  inputCost: number;
  outputCost: number;
  maxContext: number;
  speed: 'fast' | 'medium' | 'slow';
  strengths: string[];
}

export interface ProviderInfo {
  name: string;
  color: string;       // Tailwind color class
  borderColor: string;
  models: ModelInfo[];
}

// ── Providers ────────────────────────────────────────────

export const PROVIDERS: ProviderInfo[] = [
  {
    name: 'Groq',
    color: 'text-orange-400',
    borderColor: 'border-orange-500/40',
    models: [
      {
        id: 'llama-3.1-8b-instant',
        displayName: 'Llama 8B',
        provider: 'Groq',
        inputCost: 0.05,
        outputCost: 0.08,
        maxContext: 131_072,
        speed: 'fast',
        strengths: ['classification'],
      },
      {
        id: 'openai/gpt-oss-20b',
        displayName: 'GPT-OSS 20B',
        provider: 'Groq',
        inputCost: 0.075,
        outputCost: 0.30,
        maxContext: 131_072,
        speed: 'fast',
        strengths: ['qa', 'classification'],
      },
    ],
  },
  {
    name: 'xAI',
    color: 'text-blue-400',
    borderColor: 'border-blue-500/40',
    models: [
      {
        id: 'grok-3-fast',
        displayName: 'Grok 4.1 Fast',
        provider: 'xAI',
        inputCost: 0.20,
        outputCost: 0.50,
        maxContext: 2_000_000,
        speed: 'fast',
        strengths: ['long_context', 'research'],
      },
      {
        id: 'grok-code-fast-1',
        displayName: 'Grok Code Fast',
        provider: 'xAI',
        inputCost: 0.20,
        outputCost: 1.50,
        maxContext: 131_072,
        speed: 'fast',
        strengths: ['code', 'interactive_coding', 'test_generation'],
      },
    ],
  },
  {
    name: 'MiniMax',
    color: 'text-purple-400',
    borderColor: 'border-purple-500/40',
    models: [
      {
        id: 'MiniMax-M1',
        displayName: 'M1',
        provider: 'MiniMax',
        inputCost: 0.30,
        outputCost: 1.20,
        maxContext: 1_000_000,
        speed: 'slow',
        strengths: ['long_context', 'reasoning'],
      },
      {
        id: 'MiniMax-M2.1',
        displayName: 'M2.1 Coder',
        provider: 'MiniMax',
        inputCost: 0.30,
        outputCost: 1.20,
        maxContext: 200_000,
        speed: 'medium',
        strengths: ['code', 'code_review', 'test_generation', 'interactive_coding'],
      },
    ],
  },
  {
    name: 'Anthropic',
    color: 'text-amber-400',
    borderColor: 'border-amber-500/40',
    models: [
      {
        id: 'claude-opus-4-6',
        displayName: 'Opus 4.6',
        provider: 'Anthropic',
        inputCost: 15.0,
        outputCost: 75.0,
        maxContext: 200_000,
        speed: 'slow',
        strengths: ['architecture', 'security', 'security_audit'],
      },
      {
        id: 'claude-sonnet-4-5',
        displayName: 'Sonnet 4.5',
        provider: 'Anthropic',
        inputCost: 3.0,
        outputCost: 15.0,
        maxContext: 200_000,
        speed: 'fast',
        strengths: ['interactive_coding', 'code'],
      },
      {
        id: 'claude-haiku-4-5',
        displayName: 'Haiku 4.5',
        provider: 'Anthropic',
        inputCost: 1.0,
        outputCost: 5.0,
        maxContext: 200_000,
        speed: 'fast',
        strengths: ['qa', 'quick_qa'],
      },
    ],
  },
  {
    name: 'OpenAI',
    color: 'text-green-400',
    borderColor: 'border-green-500/40',
    models: [
      {
        id: 'gpt-5',
        displayName: 'GPT-5',
        provider: 'OpenAI',
        inputCost: 2.50,
        outputCost: 20.0,
        maxContext: 400_000,
        speed: 'medium',
        strengths: ['reasoning', 'research', 'architecture'],
      },
      {
        id: 'gpt-5-nano',
        displayName: 'GPT-5 Nano',
        provider: 'OpenAI',
        inputCost: 0.10,
        outputCost: 0.80,
        maxContext: 128_000,
        speed: 'fast',
        strengths: ['classification'],
      },
    ],
  },
  {
    name: 'DeepSeek',
    color: 'text-cyan-300',
    borderColor: 'border-cyan-500/40',
    models: [
      {
        id: 'deepseek-chat',
        displayName: 'V3.2',
        provider: 'DeepSeek',
        inputCost: 0.28,
        outputCost: 0.42,
        maxContext: 128_000,
        speed: 'medium',
        strengths: ['pattern_learning', 'research', 'documentation'],
      },
    ],
  },
];

// ── Routing Table (11 use cases) ─────────────────────────

export interface RouteEntry {
  useCase: string;
  displayName: string;
  primaryProvider: string;
  primaryModel: string;
  fallbackProvider: string;
  fallbackModel: string;
}

export const ROUTING_TABLE: RouteEntry[] = [
  { useCase: 'architecture', displayName: 'Architecture Review', primaryProvider: 'Anthropic', primaryModel: 'Opus 4.6', fallbackProvider: 'OpenAI', fallbackModel: 'GPT-5' },
  { useCase: 'interactive_coding', displayName: 'Interactive Coding', primaryProvider: 'Anthropic', primaryModel: 'Sonnet 4.5', fallbackProvider: 'xAI', fallbackModel: 'Grok Code Fast' },
  { useCase: 'classification', displayName: 'Classification', primaryProvider: 'Groq', primaryModel: 'Llama 8B', fallbackProvider: 'OpenAI', fallbackModel: 'GPT-5 Nano' },
  { useCase: 'long_context', displayName: 'Long Context', primaryProvider: 'xAI', primaryModel: 'Grok 4.1 Fast', fallbackProvider: 'MiniMax', fallbackModel: 'M1' },
  { useCase: 'research', displayName: 'Research', primaryProvider: 'xAI', primaryModel: 'Grok 4.1 Fast', fallbackProvider: 'DeepSeek', fallbackModel: 'V3.2' },
  { useCase: 'quick_qa', displayName: 'Quick Q&A', primaryProvider: 'Groq', primaryModel: 'GPT-OSS 20B', fallbackProvider: 'Anthropic', fallbackModel: 'Haiku 4.5' },
  { useCase: 'code_review', displayName: 'Code Review', primaryProvider: 'MiniMax', primaryModel: 'M2.1 Coder', fallbackProvider: 'Anthropic', fallbackModel: 'Sonnet 4.5' },
  { useCase: 'test_generation', displayName: 'Test Generation', primaryProvider: 'MiniMax', primaryModel: 'M2.1 Coder', fallbackProvider: 'xAI', fallbackModel: 'Grok Code Fast' },
  { useCase: 'documentation', displayName: 'Documentation', primaryProvider: 'DeepSeek', primaryModel: 'V3.2', fallbackProvider: 'Groq', fallbackModel: 'Llama 8B' },
  { useCase: 'security_audit', displayName: 'Security Audit', primaryProvider: 'Anthropic', primaryModel: 'Opus 4.6', fallbackProvider: 'OpenAI', fallbackModel: 'GPT-5' },
  { useCase: 'pattern_learning', displayName: 'Pattern Learning', primaryProvider: 'DeepSeek', primaryModel: 'V3.2', fallbackProvider: 'Groq', fallbackModel: 'Llama 8B' },
];

// ── Anthropic-only baseline for savings calculations ─────

export const ANTHROPIC_BASELINE = {
  simple: { model: 'Haiku 4.5', inputCost: 1.0, outputCost: 5.0 },
  moderate: { model: 'Sonnet 4.5', inputCost: 3.0, outputCost: 15.0 },
  complex: { model: 'Opus 4.6', inputCost: 15.0, outputCost: 75.0 },
};

// ── Helper: Get all models flat ──────────────────────────

export function getAllModels(): ModelInfo[] {
  return PROVIDERS.flatMap((p) => p.models);
}

export function getProviderByName(name: string): ProviderInfo | undefined {
  return PROVIDERS.find((p) => p.name === name);
}
